import React from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';

import {
  useGetUserFeedbackJiraLinksQuery,
  useGetUserFeedbackQuery,
  useSubmitUserFeedbackMutation,
} from 'services/api/userFeedback';

import {
  CreateUserFeedbackRequest,
  UserFeedbackActor,
  UserFeedbackCategory,
  UserFeedbackPageContext,
  UserFeedbackRecord,
  UserFeedbackScreenCapture,
  UserFeedbackStatus,
} from '../types';
import {
  formatFeedbackDateTime,
  getFeedbackCategoryVariant,
  getFeedbackErrorMessage,
  getFeedbackStatusVariant,
} from '../utils';
import { UserFeedbackModalStyles } from './style';

type UserFeedbackModalProps = {
  onHide: () => void;
  pageContext: UserFeedbackPageContext;
  reporter: UserFeedbackActor;
  screenCapture?: UserFeedbackScreenCapture | null;
  show: boolean;
};

type FeedbackModalView = 'new' | 'mine';

const DEFAULT_CATEGORY: UserFeedbackCategory = 'issue';
const DEFAULT_STATUS_FILTER: UserFeedbackStatus = 'open';

const VIEW_OPTIONS: Array<{ label: string; value: FeedbackModalView }> = [
  { label: 'New', value: 'new' },
  { label: 'My tickets', value: 'mine' },
];

const STATUS_FILTER_OPTIONS: Array<{ label: string; value: UserFeedbackStatus }> =
  [
    { label: 'Open', value: 'open' },
    { label: 'Resolved', value: 'resolved' },
  ];

const normalizeSearchValue = (value?: string | null) =>
  String(value || '').trim().toLowerCase();

const getFeedbackTitle = (feedback: UserFeedbackRecord) =>
  feedback.summary || feedback.details.slice(0, 80) || 'Untitled feedback';

const getReporterLabel = (actor: UserFeedbackActor) =>
  actor.email ? `${actor.name} (${actor.email})` : actor.name;

const getJiraTicketLabel = (feedback: UserFeedbackRecord) =>
  feedback.jira?.issueKey ? `Jira ${feedback.jira.issueKey}` : '';

const getJiraHandoffLabel = (feedback: UserFeedbackRecord) => {
  const ticketLabel = getJiraTicketLabel(feedback);
  if (ticketLabel) return ticketLabel;

  if (feedback.jira?.status === 'failed') {
    return 'Jira handoff needs attention';
  }

  if (
    feedback.jira?.status === 'disabled' ||
    feedback.jira?.status === 'not_configured'
  ) {
    return 'Saved in portal';
  }

  return 'Jira handoff pending';
};

const isSameActor = (
  left: UserFeedbackActor,
  right: UserFeedbackActor,
) => {
  const leftEmail = normalizeSearchValue(left.email);
  const rightEmail = normalizeSearchValue(right.email);
  if (leftEmail && rightEmail && leftEmail === rightEmail) return true;

  const leftId = normalizeSearchValue(left.id);
  const rightId = normalizeSearchValue(right.id);
  return !!leftId && !!rightId && leftId === rightId;
};

const matchesFeedbackSearch = (
  feedback: UserFeedbackRecord,
  searchTerm: string,
) => {
  const normalizedSearch = normalizeSearchValue(searchTerm);
  if (!normalizedSearch) return true;

  const searchableText = [
    feedback.summary,
    feedback.details,
    feedback.category,
    feedback.status,
    feedback.page.label,
    feedback.page.path,
    feedback.page.search,
    feedback.page.podName,
    feedback.reporter.name,
    feedback.reporter.email,
    feedback.jira?.issueKey,
  ]
    .map(value => normalizeSearchValue(value))
    .join(' ');

  return searchableText.includes(normalizedSearch);
};

const getFilteredFeedback = ({
  feedbackRecords,
  reporter,
  searchTerm,
  status,
  view,
}: {
  feedbackRecords: UserFeedbackRecord[];
  reporter: UserFeedbackActor;
  searchTerm: string;
  status: UserFeedbackStatus;
  view: FeedbackModalView;
}) =>
  feedbackRecords
    .filter(feedback => (view === 'mine' ? isSameActor(feedback.reporter, reporter) : true))
    .filter(feedback => feedback.status === status)
    .filter(feedback => matchesFeedbackSearch(feedback, searchTerm))
    .sort(
      (left, right) =>
        new Date(right.updatedAt || right.createdAt).getTime() -
        new Date(left.updatedAt || left.createdAt).getTime(),
    );

export default function UserFeedbackModal({
  onHide,
  pageContext,
  reporter,
  screenCapture,
  show,
}: UserFeedbackModalProps) {
  const [activeView, setActiveView] =
    React.useState<FeedbackModalView>('new');
  const [category, setCategory] =
    React.useState<UserFeedbackCategory>(DEFAULT_CATEGORY);
  const [summary, setSummary] = React.useState('');
  const [details, setDetails] = React.useState('');
  const [clientTimestamp, setClientTimestamp] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [ticketStatusFilter, setTicketStatusFilter] =
    React.useState<UserFeedbackStatus>(DEFAULT_STATUS_FILTER);
  const [ticketSearch, setTicketSearch] = React.useState('');
  const [selectedFeedbackId, setSelectedFeedbackId] =
    React.useState<string | null>(null);

  const { data: feedbackRecords = [], error: listError, isFetching } =
    useGetUserFeedbackQuery(
      { scope: 'mine' },
      {
        pollingInterval: show && activeView === 'mine' ? 5000 : 0,
        skip: !show,
      },
    );
  const { data: jiraLinks } = useGetUserFeedbackJiraLinksQuery(undefined, {
    skip: !show,
  });
  const [submitUserFeedback, submitState] = useSubmitUserFeedbackMutation();

  React.useEffect(() => {
    if (!show) return;

    setActiveView('new');
    setCategory(DEFAULT_CATEGORY);
    setSummary('');
    setDetails('');
    setErrorMessage('');
    setTicketStatusFilter(DEFAULT_STATUS_FILTER);
    setTicketSearch('');
    setSelectedFeedbackId(null);
    setClientTimestamp(new Date().toISOString());
  }, [show]);

  const visibleFeedback = React.useMemo(
    () =>
      getFilteredFeedback({
        feedbackRecords,
        reporter,
        searchTerm: ticketSearch,
        status: ticketStatusFilter,
        view: activeView,
      }),
    [activeView, feedbackRecords, reporter, ticketSearch, ticketStatusFilter],
  );

  const selectedFeedback = React.useMemo(
    () =>
      visibleFeedback.find(item => item.id === selectedFeedbackId) ||
      visibleFeedback[0] ||
      null,
    [selectedFeedbackId, visibleFeedback],
  );

  const myOpenCount = React.useMemo(
    () =>
      feedbackRecords.filter(
        item => item.status === 'open' && isSameActor(item.reporter, reporter),
      ).length,
    [feedbackRecords, reporter],
  );

  React.useEffect(() => {
    if (activeView === 'new') return;

    if (!visibleFeedback.length) {
      setSelectedFeedbackId(null);
      return;
    }

    if (!selectedFeedbackId || !visibleFeedback.some(item => item.id === selectedFeedbackId)) {
      setSelectedFeedbackId(visibleFeedback[0].id);
    }
  }, [activeView, selectedFeedbackId, visibleFeedback]);

  const handleViewChange = (view: FeedbackModalView) => {
    setActiveView(view);
    setErrorMessage('');

    if (view !== 'new') {
      setTicketStatusFilter(DEFAULT_STATUS_FILTER);
    }
  };

  const handleSubmit = async () => {
    const trimmedDetails = details.trim();
    if (!trimmedDetails) {
      setErrorMessage('Please add a few details before submitting.');
      return;
    }

    const payload: CreateUserFeedbackRequest = {
      category,
      summary: summary.trim(),
      details: trimmedDetails,
      page: {
        ...pageContext,
        url:
          typeof window !== 'undefined'
            ? `${window.location.origin}${pageContext.path}${pageContext.search || ''}`
            : pageContext.url,
      },
      reporter,
      clientTimestamp: clientTimestamp || new Date().toISOString(),
      clientTimeZone:
        typeof window !== 'undefined'
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : undefined,
      clientLocale:
        typeof window !== 'undefined'
          ? window.navigator.language
          : undefined,
      screenCapture: screenCapture || null,
      userAgent:
        typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
    };

    try {
      const createdFeedback = await submitUserFeedback(payload).unwrap();
      toast.success('Feedback submitted. Jira ticket creation is queued.');
      setDetails('');
      setSummary('');
      setCategory(DEFAULT_CATEGORY);
      setActiveView('mine');
      setTicketStatusFilter('open');
      setSelectedFeedbackId(createdFeedback.id);
    } catch (error) {
      setErrorMessage(
        getFeedbackErrorMessage(
          error,
          'Unable to submit feedback right now. Please try again.',
        ),
      );
    }
  };

  const reporterLabel = getReporterLabel(reporter);

  const renderNewFeedbackForm = () => (
    <>
      <div className="feedback-prefill-grid">
        <Form.Group className="feedback-field--full">
          <Form.Label className="feedback-label">Current page</Form.Label>
          <Form.Control
            className="feedback-readonly"
            plaintext={false}
            readOnly
            value={`${pageContext.label} (${pageContext.path}${pageContext.search || ''})`}
          />
        </Form.Group>

        <Form.Group>
          <Form.Label className="feedback-label">Timestamp</Form.Label>
          <Form.Control
            className="feedback-readonly"
            plaintext={false}
            readOnly
            value={formatFeedbackDateTime(clientTimestamp)}
          />
        </Form.Group>

        <Form.Group>
          <Form.Label className="feedback-label">Logged in user</Form.Label>
          <Form.Control
            className="feedback-readonly"
            plaintext={false}
            readOnly
            value={reporterLabel}
          />
        </Form.Group>
      </div>

      <Form.Group className="mb-3">
        <Form.Label className="feedback-label">Feedback type</Form.Label>
        <Form.Control
          as="select"
          onChange={event =>
            setCategory(event.target.value as UserFeedbackCategory)
          }
          value={category}
        >
          <option value="issue">Report an issue</option>
          <option value="feedback">General feedback</option>
        </Form.Control>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label className="feedback-label">
          Short summary <span className="text-muted">(optional)</span>
        </Form.Label>
        <Form.Control
          maxLength={200}
          onChange={event => setSummary(event.target.value)}
          placeholder="What would you like us to look into?"
          value={summary}
        />
      </Form.Group>

      <Form.Group>
        <Form.Label className="feedback-label">Additional details</Form.Label>
        <Form.Control
          as="textarea"
          maxLength={5000}
          onChange={event => setDetails(event.target.value)}
          placeholder="Describe the issue, what you were trying to do, and anything else that would help us reproduce it."
          value={details}
        />
        <p className="feedback-helper-text">
          The current page, timestamp, and your user information will be
          included automatically.
        </p>
        <div
          className={`feedback-screenshot-pill${screenCapture ? ' is-ready' : ''}`}
        >
          {screenCapture
            ? 'Current screen snapshot will be attached to the Jira ticket.'
            : 'Screen snapshot could not be captured; the ticket will still be created.'}
        </div>
      </Form.Group>
    </>
  );

  const renderFeedbackList = () => {
    if (isFetching) {
      return (
        <div className="feedback-modal-empty-state">
          <Spinner animation="border" size="sm" />
          <span>Loading tickets...</span>
        </div>
      );
    }

    if (!visibleFeedback.length) {
      return (
        <div className="feedback-modal-empty-state">
          No {ticketStatusFilter} tickets match this view.
        </div>
      );
    }

    return (
      <div className="feedback-ticket-list" role="list">
        {visibleFeedback.map(feedback => {
          const isSelected = feedback.id === selectedFeedback?.id;
          return (
            <button
              className={`feedback-ticket-card${isSelected ? ' is-selected' : ''}`}
              key={feedback.id}
              onClick={() => setSelectedFeedbackId(feedback.id)}
              type="button"
            >
              <div className="feedback-ticket-card-head">
                <Badge bg={getFeedbackCategoryVariant(feedback.category)}>
                  {feedback.category}
                </Badge>
                <span className="feedback-ticket-updated">
                  {formatFeedbackDateTime(feedback.updatedAt)}
                </span>
              </div>
              <strong>{getFeedbackTitle(feedback)}</strong>
              <div className="feedback-ticket-card-contexts">
                <span className="feedback-ticket-context">
                  {feedback.page.label}
                </span>
                <span className="feedback-ticket-context is-jira">
                  {getJiraHandoffLabel(feedback)}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  const renderFeedbackDetails = () => {
    if (!selectedFeedback) {
      return (
        <div className="feedback-modal-empty-state">
          Select a ticket to review its Jira handoff details.
        </div>
      );
    }

    return (
      <div className="feedback-thread">
        <div className="feedback-thread-header">
          <div>
            <h3>{getFeedbackTitle(selectedFeedback)}</h3>
            <div className="feedback-thread-badges">
              <Badge bg={getFeedbackCategoryVariant(selectedFeedback.category)}>
                {selectedFeedback.category}
              </Badge>
              <Badge bg={getFeedbackStatusVariant(selectedFeedback.status)}>
                {selectedFeedback.status}
              </Badge>
              {selectedFeedback.jira?.issueKey ? (
                <a
                  className="feedback-jira-ticket-link"
                  href={selectedFeedback.jira.issueUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  {getJiraTicketLabel(selectedFeedback)}
                </a>
              ) : null}
            </div>
          </div>
          <span className="feedback-ticket-updated">
            {selectedFeedback.reporter.name}
          </span>
        </div>

        <div className="feedback-thread-meta">
          <span>{selectedFeedback.page.label}</span>
          <span>{formatFeedbackDateTime(selectedFeedback.createdAt)}</span>
          {selectedFeedback.resolvedAt ? (
            <span>Resolved {formatFeedbackDateTime(selectedFeedback.resolvedAt)}</span>
          ) : null}
        </div>

        <div className="feedback-thread-message">
          <span className="feedback-thread-section-label">Submitted details</span>
          <p>{selectedFeedback.details}</p>
        </div>

        <div className="feedback-jira-cta-card">
          <div className="feedback-jira-cta-copy">
            <strong>Continue the conversation in Jira</strong>
            <span>
              Comments, attachments, status updates, and owner changes should
              happen directly on the Jira ticket.
            </span>
          </div>
          {selectedFeedback.jira?.issueUrl ? (
            <Button
              as="a"
              href={selectedFeedback.jira.issueUrl}
              rel="noreferrer"
              target="_blank"
              variant="primary"
            >
              Open {selectedFeedback.jira.issueKey || 'Jira ticket'}
            </Button>
          ) : (
            <span className="feedback-ticket-context">
              {getJiraHandoffLabel(selectedFeedback)}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderTicketBrowser = () => (
    <>
      <div className="feedback-browser-toolbar">
        <div className="feedback-status-switch">
          {STATUS_FILTER_OPTIONS.map(option => (
            <button
              className={ticketStatusFilter === option.value ? 'is-active' : ''}
              key={option.value}
              onClick={() => setTicketStatusFilter(option.value)}
              type="button"
            >
              {option.label}
            </button>
          ))}
        </div>
        <Form.Control
          className="feedback-search-input"
          onChange={event => setTicketSearch(event.target.value)}
          placeholder="Search your tickets, pages, or Jira IDs"
          value={ticketSearch}
        />
        {jiraLinks?.openIssuesUrl ? (
          <a
            className="feedback-jira-search-link"
            href={jiraLinks.openIssuesUrl}
            rel="noreferrer"
            target="_blank"
          >
            View all open tickets in Jira
          </a>
        ) : null}
      </div>

      <div className="feedback-ticket-shell">
        <section>{renderFeedbackList()}</section>
        <aside>{renderFeedbackDetails()}</aside>
      </div>
    </>
  );

  return (
    <UserFeedbackModalStyles
      centered
      dialogClassName={
        activeView === 'new'
          ? 'feedback-dialog feedback-dialog--new'
          : 'feedback-dialog feedback-dialog--browser'
      }
      onHide={onHide}
      show={show}
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <span className="feedback-title-mark">?</span>
          <span>
            Feedback
            <small>Report issues here, then continue follow-ups in Jira.</small>
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errorMessage || (activeView !== 'new' && listError) ? (
          <Alert
            onClose={() => setErrorMessage('')}
            dismissible={!!errorMessage}
            variant="danger"
          >
            {errorMessage ||
              getFeedbackErrorMessage(
                listError,
                'Unable to load feedback tickets right now.',
              )}
          </Alert>
        ) : null}

        <div className="feedback-modal-tabs">
          {VIEW_OPTIONS.map(option => (
            <button
              className={activeView === option.value ? 'is-active' : ''}
              key={option.value}
              onClick={() => handleViewChange(option.value)}
              type="button"
            >
              <span>{option.label}</span>
              {option.value === 'mine' && myOpenCount ? (
                <Badge bg="light" text="dark">
                  {myOpenCount}
                </Badge>
              ) : null}
            </button>
          ))}
        </div>

        {activeView === 'new' ? renderNewFeedbackForm() : renderTicketBrowser()}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onHide} variant="light">
          Close
        </Button>
        {activeView === 'new' ? (
          <Button
            disabled={submitState.isLoading}
            onClick={handleSubmit}
            variant="primary"
          >
            {submitState.isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="mr-2" />
                Submitting...
              </>
            ) : (
              'Submit'
            )}
          </Button>
        ) : null}
      </Modal.Footer>
    </UserFeedbackModalStyles>
  );
}
